import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from './auth/msalConfig';
import { fetchMe } from './api/meApi';
import { TakeSurvey } from './pages/TakeSurvey';
import { SurveyComplete } from './pages/SurveyComplete';
import { HireRating, HireRatingComplete } from './pages/HireRating';
import { Employees } from './pages/admin/Employees';
import { SurveysList } from './pages/admin/SurveysList';
import { HistoricalImport } from './pages/admin/HistoricalImport';
import { HireRatingRounds } from './pages/admin/HireRatingRounds';
import { ResponseRatesPage } from './pages/admin/ResponseRatesPage';
import { CreateSurvey } from './pages/admin/CreateSurvey';

const navLink: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  padding: '0.25rem 0.5rem',
  borderRadius: 4,
};

const centered: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', minHeight: '100vh', gap: '1.5rem', textAlign: 'center', padding: '1.5rem',
};

const DEV_BYPASS = import.meta.env.DEV && !import.meta.env.VITE_AZURE_CLIENT_ID;

/**
 * Wraps every admin page. Gates access: not signed in → sign-in prompt;
 * signed in but lacking the Admin role → access-restricted; admin → the shell.
 * (Survey-taking and manager hire-rating routes live outside this shell.)
 */
function AdminShell({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const isAuthed = useIsAuthenticated() || DEV_BYPASS;
  const [access, setAccess] = useState<'loading' | 'admin' | 'denied'>(DEV_BYPASS ? 'admin' : 'loading');

  useEffect(() => {
    if (DEV_BYPASS) { setAccess('admin'); return; }
    if (!isAuthed || !accounts[0]) return;
    let cancelled = false;
    instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] })
      .then(r => fetchMe(r.accessToken))
      .then(me => { if (!cancelled) setAccess(me.isAdmin ? 'admin' : 'denied'); })
      .catch(() => { if (!cancelled) setAccess('denied'); });
    return () => { cancelled = true; };
  }, [isAuthed, accounts, instance]);

  if (!isAuthed) {
    return (
      <div style={centered}>
        <img src="/gridworks-logo.png" alt="GridPulse" style={{ height: 52, display: 'block' }} />
        <button className="btn btn-primary" style={{ minWidth: 200 }}
          onClick={() => instance.loginRedirect(loginRequest)}>
          Sign in with Microsoft
        </button>
      </div>
    );
  }

  if (access === 'loading') {
    return <div style={centered}><p style={{ color: 'var(--color-text-muted)' }}>Checking access…</p></div>;
  }

  if (access === 'denied') {
    return (
      <div style={centered}>
        <img src="/gridworks-logo.png" alt="GridPulse" style={{ height: 52, display: 'block' }} />
        <div>
          <h2 style={{ margin: '0 0 0.5rem' }}>Access restricted</h2>
          <p style={{ color: 'var(--color-text-muted)', maxWidth: 440 }}>
            Your account doesn’t have GridPulse admin access. Contact an administrator if you believe this is an error.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => instance.logoutRedirect()}>Sign out</button>
      </div>
    );
  }

  const displayName = accounts[0]?.name ?? (DEV_BYPASS ? 'Dev User (local)' : '');
  return (
    <div style={{ fontFamily: 'var(--font-base)', minHeight: '100vh', background: '#f7f8fc' }}>
      {DEV_BYPASS && (
        <div style={{
          background: '#fff3cd', color: '#856404', fontSize: '0.78rem',
          textAlign: 'center', padding: '3px 0', borderBottom: '1px solid #ffeaa7',
        }}>
          ⚠ Dev mode — auth bypassed, no real Entra ID token
        </div>
      )}
      <header style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '0 1.5rem',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
      }}>
        <img src="/gridworks-logo.png" alt="GridPulse" style={{ height: 32, display: 'block' }} />
        <nav style={{ display: 'flex', gap: '0.25rem', overflow: 'auto' }}>
          <NavLink to="/admin/surveys" style={navLink}>Surveys</NavLink>
          <NavLink to="/admin/employees" style={navLink}>Employees</NavLink>
          <NavLink to="/admin/response-rates" style={navLink}>Response Rates</NavLink>
          <NavLink to="/admin/hire-rating-rounds" style={navLink}>Hire Ratings</NavLink>
          <NavLink to="/admin/historical-import" style={navLink}>Import History</NavLink>
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{displayName}</span>
          {!DEV_BYPASS && (
            <button className="btn btn-ghost" style={{ minHeight: 32, padding: '0 0.75rem', fontSize: '0.85rem' }}
              onClick={() => instance.logoutRedirect()}>
              Sign out
            </button>
          )}
        </div>
      </header>
      <main style={{ padding: '1.5rem' }}>{children}</main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* ── Anonymous (token-gated survey taking) ─────────────────────── */}
        <Route path="/s/:token"        element={<TakeSurvey />} />
        <Route path="/survey/complete" element={<SurveyComplete />} />

        {/* ── Manager hire-rating (authenticated, non-admin) ────────────── */}
        <Route path="/hire-rating/:id"      element={<HireRating />} />
        <Route path="/hire-rating/complete" element={<HireRatingComplete />} />

        {/* ── Admin area (gated by AdminShell: sign-in → admin role) ────── */}
        <Route path="/admin/surveys"            element={<AdminShell><SurveysList /></AdminShell>} />
        <Route path="/admin/surveys/new"        element={<AdminShell><CreateSurvey /></AdminShell>} />
        <Route path="/admin/employees"          element={<AdminShell><Employees /></AdminShell>} />
        <Route path="/admin/response-rates"     element={<AdminShell><ResponseRatesPage /></AdminShell>} />
        <Route path="/admin/hire-rating-rounds" element={<AdminShell><HireRatingRounds /></AdminShell>} />
        <Route path="/admin/historical-import"  element={<AdminShell><HistoricalImport /></AdminShell>} />

        <Route path="/*" element={
          <AdminShell><p style={{ color: 'var(--color-text-muted)' }}>Dashboard coming soon.</p></AdminShell>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
